import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { provideRouter } from '@angular/router';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { routes } from './app.routes';
import { provideNzIcons } from './icons-provider';
import { NZ_DATE_CONFIG, provideNzI18n } from 'ng-zorro-antd/i18n';
import { DatePipe, registerLocaleData } from '@angular/common';
import fr from '@angular/common/locales/fr';
import en from '@angular/common/locales/en';
import de from '@angular/common/locales/de';
import es from '@angular/common/locales/es';
import zh from '@angular/common/locales/zh';
import { FormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Chart } from 'chart.js';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '@environment/environment';
import { customFrFR } from '@config/custom-config-FR-fr';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import noDataPlugin from '@component/kpi/charts/chart-plugins/chart-no-data-plugin';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import { provideTranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { UtcDateFormatPipe } from '@shared/pipe/UtcDateFormatPipe';
import xLabelClickPlugin from '@component/kpi/charts/chart-plugins/chart-x-label-click-plugin';
import { xLabelHoverPlugin } from '@component/kpi/charts/chart-plugins/chart-x-label-hover-plugin';

// Register locale data for supported languages for html elements like datepicker, select, etc.
[
  [fr, ['fr', 'fr-FR', 'fr-fr']],
  [en, ['en', 'en-US', 'en-us']],
  [de, ['de', 'de-DE', 'de-de']],
  [es, ['es', 'es-ES', 'es-es']],
  [zh, ['zh', 'zh-CN', 'zh-cn']]
].forEach(([localeData, localeIds]) => {
  (localeIds as string[]).forEach((localeId) => registerLocaleData(localeData, localeId));
});
Chart.register(noDataPlugin);
Chart.register(ChartDataLabels);
Chart.register(annotationPlugin);
Chart.register(xLabelHoverPlugin);
Chart.register(xLabelClickPlugin);

// Keep a bit more room under x-axis labels across all charts.
Chart.defaults.layout.padding = {
  top: 0,
  right: 0,
  bottom: 18,
  left: 0,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideNzIcons(),
    provideNzI18n(customFrFR),
    importProvidersFrom(FormsModule, NzDrawerModule),
    // provideAnimationsAsync(),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()), // withInterceptorsFromDi must be ativated to use interceptor !!
    provideTranslateService(
      {
        loader: provideTranslateHttpLoader({
          prefix: '/assets/i18n/',
          suffix: '.json'
        }),
        fallbackLang: 'fr',
        lang: 'fr',
      }
    ),
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    {
      provide: NZ_DATE_CONFIG,
      useValue: {
        firstDayOfWeek: 1, // Week start on monday instead of sunday
      }
    },
    provideCharts(withDefaultRegisterables()), // change locale to fr to display correct date format
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: AuthInterceptor,
    //   multi: true,
    // },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    UtcDateFormatPipe,
    DatePipe
  ]
  // providers: [provideZoneChangeDetection({ eventCoalescing: true }), provideRouter(routes), provideNzIcons(), provideNzI18n(fr_FR), importProvidersFrom(FormsModule), provideAnimationsAsync(), provideHttpClient()]
};

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback: (logLevel, message, containsPii) => {
        },
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false
      }
    },
  });
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.msalConfig.auth.apiScopeUrl],
    },
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string> | null>();
  protectedResourceMap.set(
    environment.apiConfig.webApiUri,
    environment.msalConfig.auth.apiScopeUrl
  );

  // protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
  //   'user.read',
  // ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}
